import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import {
  FileText, RefreshCw, Truck, MapPin, Clock, CheckCircle, AlertTriangle, Package, Filter,
  User, Navigation, ChevronRight
} from 'lucide-react';
import { DashboardLayout } from '../../layouts/DashboardLayout';
import { useAuth } from '../../contexts/AuthContext';
import { getRemissionDeliveries, getRemissionDeliveryStats, getBranches } from '../../services/api';
import { getWsUrl } from '../../config';
import { toast } from 'sonner';
import 'leaflet/dist/leaflet.css';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

const STATUS_LABELS = {
  pending: 'Pendiente', assigned: 'Por recoger', picked_up: 'Recogida',
  in_transit: 'En camino', delivered: 'Entregada', failed: 'Fallida', cancelled: 'Cancelada',
};

const STATUS_COLORS = {
  pending: 'bg-amber-100 text-amber-700',
  assigned: 'bg-blue-100 text-blue-700',
  picked_up: 'bg-indigo-100 text-indigo-700',
  in_transit: 'bg-purple-100 text-purple-700',
  delivered: 'bg-green-100 text-green-700',
  failed: 'bg-red-100 text-red-700',
  cancelled: 'bg-gray-200 text-gray-600',
};

const ACTIVE_STATUSES = ['assigned', 'picked_up', 'in_transit'];

// Icono personalizado para pin del chofer
const driverIcon = new L.DivIcon({
  html: `<div style="background: #2563eb; width: 32px; height: 32px; border-radius: 50%; display:flex; align-items:center; justify-content:center; border: 3px solid white; box-shadow: 0 2px 6px rgba(0,0,0,0.3);">
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M14 18V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v11a1 1 0 0 0 1 1h2"/><path d="M15 18H9"/><path d="M19 18h2a1 1 0 0 0 1-1v-3.65a1 1 0 0 0-.22-.624l-3.48-4.35A1 1 0 0 0 17.52 8H14"/><circle cx="17" cy="18" r="2"/><circle cx="7" cy="18" r="2"/></svg>
  </div>`,
  className: '',
  iconSize: [32, 32],
  iconAnchor: [16, 16],
});

const StatCard = ({ icon: Icon, label, value, tone = 'blue' }) => {
  const toneMap = {
    amber: 'bg-amber-50 text-amber-700',
    blue: 'bg-blue-50 text-blue-700',
    purple: 'bg-purple-50 text-purple-700',
    green: 'bg-green-50 text-green-700',
    red: 'bg-red-50 text-red-700',
  };
  return (
    <div className="bg-white rounded-xl p-4 border border-gray-100">
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${toneMap[tone] || toneMap.blue}`}>
          <Icon className="w-5 h-5" />
        </div>
        <div>
          <div className="text-xs text-gray-500">{label}</div>
          <div className="text-xl font-semibold text-gray-900">{value}</div>
        </div>
      </div>
    </div>
  );
};

const DriverRouteCard = ({ driverId, driverName, activeRoute, deliveredToday, location, onFocus }) => {
  const total = activeRoute.length + deliveredToday;
  const done = deliveredToday;
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;

  const next = activeRoute[0];
  return (
    <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
      <div className="px-4 py-3 border-b border-gray-100 flex items-center gap-3">
        <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center text-blue-700 font-bold">
          {driverName?.[0]?.toUpperCase() || 'R'}
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-gray-900 truncate">{driverName || 'Repartidor'}</div>
          <div className="text-xs text-gray-500 flex items-center gap-1">
            {location
              ? <><Navigation className="w-3 h-3 text-blue-500" /> En movimiento · últ. {location.ts ? new Date(location.ts).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' }) : '—'}</>
              : <>Sin señal GPS</>}
          </div>
        </div>
        {location && (
          <button onClick={() => onFocus(location)} className="text-xs px-3 py-1.5 bg-blue-50 text-blue-700 rounded-lg font-medium">
            Ver en mapa
          </button>
        )}
      </div>
      <div className="px-4 py-3">
        <div className="flex items-center justify-between mb-1">
          <div className="text-xs text-gray-600 font-medium">Ruta del día</div>
          <div className="text-xs font-semibold text-gray-700">
            {done} de {total} ({pct}%)
          </div>
        </div>
        <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
          <div className="h-full bg-gradient-to-r from-blue-500 to-indigo-500 transition-all" style={{ width: `${pct}%` }} />
        </div>

        {next ? (
          <div className="mt-3 bg-blue-50 border border-blue-100 rounded-lg p-3">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="text-[11px] uppercase tracking-wide text-blue-700 font-semibold">Próxima entrega</div>
                <div className="font-semibold text-gray-900 truncate">{next.folio} · {next.customer_name}</div>
                {next.delivery_address && (
                  <div className="text-xs text-gray-600 mt-0.5 flex items-start gap-1">
                    <MapPin className="w-3 h-3 text-gray-400 mt-0.5 shrink-0" />
                    <span className="break-words line-clamp-2">{next.delivery_address}</span>
                  </div>
                )}
              </div>
              <span className={`text-[11px] px-2 py-0.5 rounded-full font-medium ${STATUS_COLORS[next.status]} shrink-0`}>
                {STATUS_LABELS[next.status]}
              </span>
            </div>
          </div>
        ) : (
          <div className="mt-3 text-xs text-gray-500 italic text-center py-2">Sin entregas activas</div>
        )}

        {activeRoute.length > 1 && (
          <div className="mt-2 space-y-1">
            <div className="text-[11px] uppercase tracking-wide text-gray-500 font-semibold pl-1">Siguientes</div>
            {activeRoute.slice(1).map((d, i) => (
              <div key={d.id} className="flex items-center gap-2 text-xs text-gray-700 px-2 py-1.5 rounded-lg bg-gray-50">
                <span className="w-5 h-5 rounded-full bg-white border border-gray-200 flex items-center justify-center text-[10px] font-bold text-gray-500">
                  {i + 2}
                </span>
                <span className="font-medium truncate">{d.folio}</span>
                <span className="text-gray-500 truncate">· {d.customer_name}</span>
                <ChevronRight className="w-3 h-3 text-gray-400 ml-auto" />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const EntregasRemisiones = () => {
  const { user } = useAuth();
  const businessId = user?.business_id;
  const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;

  const [deliveries, setDeliveries] = useState([]);
  const [stats, setStats] = useState({ by_status: {} });
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [filters, setFilters] = useState({ status: '', branch_id: '' });
  const [driverLocations, setDriverLocations] = useState({});
  const [focusedLocation, setFocusedLocation] = useState(null);
  const mapRef = useRef(null);

  const wsRef = useRef(null);
  const wsReconnectRef = useRef(null);

  const refresh = useCallback(async () => {
    if (!businessId) return;
    setRefreshing(true);
    try {
      const params = {};
      if (filters.status) params.status = filters.status;
      if (filters.branch_id) params.branch_id = filters.branch_id;
      params.limit = 200;
      const [resDel, resStats] = await Promise.all([
        getRemissionDeliveries(businessId, params),
        getRemissionDeliveryStats(businessId),
      ]);
      setDeliveries(Array.isArray(resDel.data) ? resDel.data : []);
      setStats(resStats.data || { by_status: {} });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error cargando entregas');
    } finally {
      setRefreshing(false);
      setLoading(false);
    }
  }, [businessId, filters]);

  useEffect(() => { setLoading(true); refresh(); }, [refresh]);

  useEffect(() => {
    let mounted = true;
    if (!businessId) return;
    getBranches(businessId)
      .then(r => { if (mounted) setBranches(r.data || []); })
      .catch(() => {});
    return () => { mounted = false; };
  }, [businessId]);

  // WebSocket en vivo
  useEffect(() => {
    if (!businessId || !token) return;
    const connect = () => {
      try {
        const url = `${getWsUrl()}/api/v1/remissions-delivery/ws/business/${businessId}?token=${token}`;
        const ws = new WebSocket(url);
        wsRef.current = ws;
        ws.onmessage = (evt) => {
          try {
            if (evt.data === 'pong') return;
            const msg = JSON.parse(evt.data);
            if (msg.type === 'remission:new' || msg.type === 'remission:update' || msg.type === 'remission:reorder') {
              refresh();
            } else if (msg.type === 'driver:location') {
              setDriverLocations(prev => ({
                ...prev,
                [msg.driver_id]: { lat: msg.lat, lng: msg.lng, name: msg.driver_name, ts: msg.timestamp },
              }));
            }
          } catch {}
        };
        ws.onclose = () => {
          wsRef.current = null;
          wsReconnectRef.current = setTimeout(connect, 4000);
        };
        ws.onerror = () => { try { ws.close(); } catch {} };
        const pingIv = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) ws.send('ping');
        }, 30000);
        ws._pingIv = pingIv;
      } catch {
        wsReconnectRef.current = setTimeout(connect, 4000);
      }
    };
    connect();
    return () => {
      if (wsReconnectRef.current) clearTimeout(wsReconnectRef.current);
      if (wsRef.current) {
        try { clearInterval(wsRef.current._pingIv); } catch {}
        try { wsRef.current.close(); } catch {}
      }
    };
  }, [businessId, token, refresh]);

  // Agrupar por chofer: ruta activa (ordenada por sequence) + entregadas hoy
  const { driverGroups, unassignedPending } = useMemo(() => {
    const groups = new Map(); // driver_id -> { name, active: [], delivered: 0 }
    const unassigned = [];

    const todayKey = new Date().toISOString().slice(0, 10);

    for (const d of deliveries) {
      const isDeliveredToday = d.status === 'delivered' &&
        d.delivered_at && d.delivered_at.slice(0, 10) === todayKey;

      if (!d.driver_id) {
        if (d.status === 'pending') unassigned.push(d);
        continue;
      }

      if (!groups.has(d.driver_id)) {
        groups.set(d.driver_id, { id: d.driver_id, name: d.driver_name || 'Repartidor', active: [], delivered: 0 });
      }
      const g = groups.get(d.driver_id);

      if (ACTIVE_STATUSES.includes(d.status)) g.active.push(d);
      if (isDeliveredToday) g.delivered += 1;
    }

    // Ordenar activas por sequence
    for (const g of groups.values()) {
      g.active.sort((a, b) => (a.sequence ?? 0) - (b.sequence ?? 0));
    }
    // Sólo mostrar choferes con actividad (activas o entregas hoy)
    const filtered = [...groups.values()].filter(g => g.active.length > 0 || g.delivered > 0);
    return { driverGroups: filtered, unassignedPending: unassigned };
  }, [deliveries]);

  const mapCenter = useMemo(() => {
    const locs = Object.values(driverLocations);
    if (locs.length > 0) return [locs[0].lat, locs[0].lng];
    return [23.6345, -102.5528];
  }, [driverLocations]);

  const focusOnLocation = (loc) => {
    setFocusedLocation(loc);
    if (mapRef.current) {
      try { mapRef.current.flyTo([loc.lat, loc.lng], 15); } catch {}
    }
  };

  return (
    <DashboardLayout>
      <div className="p-4 md:p-6 space-y-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <FileText className="w-6 h-6 text-blue-600" /> Entregas de Remisiones
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              Monitorea en vivo las rutas de tus repartidores.
            </p>
          </div>
          <button onClick={refresh} disabled={refreshing}
            className="px-4 py-2 bg-white border border-gray-200 rounded-lg text-sm font-medium flex items-center gap-2 hover:bg-gray-50">
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            Actualizar
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatCard icon={Package} tone="amber" label="Pendientes" value={stats.by_status?.pending || 0} />
          <StatCard icon={Truck} tone="blue" label="En ruta" value={
            (stats.by_status?.assigned || 0) + (stats.by_status?.picked_up || 0) + (stats.by_status?.in_transit || 0)
          } />
          <StatCard icon={CheckCircle} tone="green" label="Entregadas" value={stats.by_status?.delivered || 0} />
          <StatCard icon={AlertTriangle} tone="red" label="Fallidas" value={stats.by_status?.failed || 0} />
        </div>

        {/* Mapa en vivo */}
        <div className="bg-white rounded-xl overflow-hidden border border-gray-100">
          <div className="px-4 py-3 border-b border-gray-100 flex items-center gap-2 flex-wrap">
            <MapPin className="w-4 h-4 text-blue-600" />
            <div className="font-medium text-gray-900">Repartidores en vivo</div>
            <span className="text-xs text-gray-500">
              ({Object.keys(driverLocations).length} con ubicación)
            </span>
          </div>
          <div className="h-80">
            <MapContainer
              center={mapCenter}
              zoom={Object.keys(driverLocations).length ? 13 : 5}
              style={{ height: '100%', width: '100%' }}
              ref={(ref) => { if (ref) mapRef.current = ref; }}
            >
              <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution="" />
              {Object.entries(driverLocations).map(([did, loc]) => (
                <Marker key={did} position={[loc.lat, loc.lng]} icon={driverIcon}>
                  <Popup>
                    <div>
                      <div className="font-semibold">{loc.name || 'Repartidor'}</div>
                      <div className="text-xs text-gray-500">
                        {loc.ts ? new Date(loc.ts).toLocaleTimeString() : ''}
                      </div>
                    </div>
                  </Popup>
                </Marker>
              ))}
            </MapContainer>
          </div>
          {focusedLocation && (
            <div className="px-4 py-2 bg-blue-50 border-t border-blue-100 text-xs text-blue-700 flex items-center justify-between">
              <span>Enfocando a {focusedLocation.name}</span>
              <button onClick={() => setFocusedLocation(null)} className="text-blue-700 font-medium">Liberar</button>
            </div>
          )}
        </div>

        {/* Rutas por chofer */}
        <div>
          <h2 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
            <User className="w-5 h-5 text-gray-500" /> Rutas por chofer
          </h2>
          {loading ? (
            <div className="bg-white rounded-xl p-10 text-center text-gray-500 text-sm border border-gray-100">Cargando...</div>
          ) : driverGroups.length === 0 ? (
            <div className="bg-white rounded-xl p-10 text-center border border-gray-100">
              <Truck className="w-10 h-10 text-gray-300 mx-auto mb-2" />
              <p className="text-sm text-gray-700 font-medium">Nadie con ruta activa</p>
              <p className="text-xs text-gray-500 mt-1">
                {unassignedPending.length > 0
                  ? `${unassignedPending.length} remisión(es) esperando ser tomadas por un chofer.`
                  : 'No hay remisiones surtidas por entregar.'}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
              {driverGroups.map(g => (
                <DriverRouteCard
                  key={g.id}
                  driverId={g.id}
                  driverName={g.name}
                  activeRoute={g.active}
                  deliveredToday={g.delivered}
                  location={driverLocations[g.id]}
                  onFocus={focusOnLocation}
                />
              ))}
            </div>
          )}

          {unassignedPending.length > 0 && (
            <div className="mt-4 bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-700 mt-0.5" />
              <div className="flex-1">
                <div className="font-semibold text-amber-900">
                  {unassignedPending.length} remisión(es) pendiente(s) sin repartidor
                </div>
                <div className="text-xs text-amber-800 mt-0.5">
                  {unassignedPending.slice(0, 3).map(d => d.folio).join(', ')}
                  {unassignedPending.length > 3 ? '…' : ''}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Filtros */}
        <div className="bg-white rounded-xl p-4 border border-gray-100 flex items-center gap-3 flex-wrap">
          <Filter className="w-4 h-4 text-gray-500" />
          <select value={filters.status} onChange={(e) => setFilters(f => ({ ...f, status: e.target.value }))}
            className="px-3 py-2 rounded-lg border border-gray-200 text-sm">
            <option value="">Todos los estados</option>
            {Object.entries(STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          <select value={filters.branch_id} onChange={(e) => setFilters(f => ({ ...f, branch_id: e.target.value }))}
            className="px-3 py-2 rounded-lg border border-gray-200 text-sm">
            <option value="">Todas las sucursales</option>
            {branches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
        </div>

        {/* Tabla detallada */}
        <div className="bg-white rounded-xl border border-gray-100">
          <div className="px-4 py-3 border-b border-gray-100 flex items-center gap-2">
            <Clock className="w-4 h-4 text-gray-500" />
            <div className="font-medium text-gray-900">Todas (filtradas)</div>
            <span className="text-xs text-gray-500">· {deliveries.length} resultados</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-left text-xs text-gray-500">
                <tr>
                  <th className="px-4 py-2">Folio</th>
                  <th className="px-4 py-2">Cliente</th>
                  <th className="px-4 py-2">Sucursal</th>
                  <th className="px-4 py-2">Repartidor</th>
                  <th className="px-4 py-2">Estado</th>
                  <th className="px-4 py-2 text-right">Total</th>
                  <th className="px-4 py-2">Recibió</th>
                  <th className="px-4 py-2">Entregado</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {deliveries.length === 0 && (
                  <tr><td colSpan={8} className="p-6 text-center text-gray-500">Sin datos</td></tr>
                )}
                {deliveries.map(d => {
                  const br = branches.find(b => b.id === d.branch_id);
                  return (
                    <tr key={d.id} className="hover:bg-gray-50">
                      <td className="px-4 py-2 font-medium text-gray-900">{d.folio || '—'}</td>
                      <td className="px-4 py-2 text-gray-700">{d.customer_name}</td>
                      <td className="px-4 py-2 text-gray-600">{br?.name || '—'}</td>
                      <td className="px-4 py-2 text-gray-700">{d.driver_name || <span className="text-gray-400">Sin asignar</span>}</td>
                      <td className="px-4 py-2">
                        <span className={`text-xs px-2 py-1 rounded-full font-medium ${STATUS_COLORS[d.status] || 'bg-gray-100 text-gray-700'}`}>
                          {STATUS_LABELS[d.status] || d.status}
                        </span>
                      </td>
                      <td className="px-4 py-2 text-right text-gray-700">${Number(d.total || 0).toFixed(2)}</td>
                      <td className="px-4 py-2 text-gray-700">{d.received_by || '—'}</td>
                      <td className="px-4 py-2 text-gray-600 text-xs">
                        {d.delivered_at ? new Date(d.delivered_at).toLocaleString() : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default EntregasRemisiones;
